<script setup>
import { useApi } from '@/utils/api'
import { useAbility } from '@casl/vue'

definePage({
  meta: {
    layout: 'default',
    action: 'read',
    subject: 'Product',
  },
})

const ability = useAbility()

// ── State ──────────────────────────────────────────────────────────────────
const search = ref('')
const isActive = ref(null)
const page = ref(1)
const perPage = ref(15)
const loading = ref(false)
const products = ref([])
const totalItems = ref(0)
const deleteDialog = ref(false)
const selectedProduct = ref(null)
const deleteLoading = ref(false)
const snackbar = ref({ show: false, text: '', color: 'success' })

const headers = [
  { title: 'Produk', key: 'name', sortable: false },
  { title: 'Kategori', key: 'category', sortable: false },
  { title: 'Varian', key: 'variants', sortable: false, align: 'center' },
  { title: 'Status', key: 'is_active', sortable: false, align: 'center' },
  { title: 'Aksi', key: 'actions', sortable: false, align: 'center' },
]

const statusOptions = [
  { title: 'Semua Status', value: null },
  { title: 'Aktif', value: true },
  { title: 'Non-aktif', value: false },
]

// ── Fetch ──────────────────────────────────────────────────────────────────
const fetchProducts = async () => {
  loading.value = true
  try {
    const query = { page: page.value, per_page: perPage.value }
    if (search.value) query.search = search.value
    if (isActive.value !== null) query.is_active = isActive.value

    // ✅ ofetch return body langsung (addendum 5A.4)
    const response = await useApi('/api/v1/products', { query }).get()
    const data = response?.data ?? response

    products.value = data?.data ?? []
    totalItems.value = data?.meta?.total ?? 0
  } catch (err) {
    showSnackbar('Gagal memuat data produk', 'error')
    console.error(err)
  } finally {
    loading.value = false
  }
}

watchDebounced(search, () => { page.value = 1; fetchProducts() }, { debounce: 400 })
watch([isActive, page], fetchProducts)
onMounted(fetchProducts)

// ── Delete ─────────────────────────────────────────────────────────────────
const confirmDelete = product => {
  selectedProduct.value = product
  deleteDialog.value = true
}

const doDelete = async () => {
  deleteLoading.value = true
  try {
    const response = await useApi(`/api/v1/products/${selectedProduct.value.id}`).delete()
    const data = response?.data ?? response
    showSnackbar(data?.message ?? 'Produk berhasil dihapus')
    deleteDialog.value = false
    fetchProducts()
  } catch (err) {
    showSnackbar(err?.data?.message ?? 'Gagal menghapus produk', 'error')
  } finally {
    deleteLoading.value = false
  }
}

// ── Helpers ────────────────────────────────────────────────────────────────
const showSnackbar = (text, color = 'success') => {
  snackbar.value = { show: true, text, color }
}

const formatRupiah = value => {
  if (value == null) return '-'

  return new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    minimumFractionDigits: 0,
  }).format(value)
}

const getVariantPriceRange = variants => {
  if (!variants?.length) return '-'
  const prices = variants.map(v => v.price)
  const min = Math.min(...prices)
  const max = Math.max(...prices)

  return min === max ? formatRupiah(min) : `${formatRupiah(min)} – ${formatRupiah(max)}`
}
</script>

<template>
  <div>
    <!-- Header -->
    <VRow class="mb-4">
      <VCol cols="12">
        <div class="d-flex align-center justify-space-between flex-wrap gap-3">
          <div>
            <h4 class="text-h5 font-weight-bold">Produk</h4>
            <p class="text-body-2 text-medium-emphasis mb-0">
              Kelola katalog produk dan varian harga
            </p>
          </div>
          <VBtn
            v-if="ability.can('create', 'Product')"
            prepend-icon="ri-add-line"
            :to="{ name: 'catalog-products-create' }"
          >
            Tambah Produk
          </VBtn>
        </div>
      </VCol>
    </VRow>

    <!-- Filters -->
    <VCard class="mb-4">
      <VCardText>
        <VRow dense>
          <VCol cols="12" md="6">
            <VTextField
              v-model="search"
              placeholder="Cari nama produk..."
              prepend-inner-icon="ri-search-line"
              clearable
              hide-details
            />
          </VCol>
          <VCol cols="12" sm="6" md="3">
            <VSelect
              v-model="isActive"
              :items="statusOptions"
              item-title="title"
              item-value="value"
              hide-details
              placeholder="Status"
            />
          </VCol>
        </VRow>
      </VCardText>
    </VCard>

    <!-- Table -->
    <VCard>
      <VDataTableServer
        :headers="headers"
        :items="products"
        :items-length="totalItems"
        :loading="loading"
        :items-per-page="perPage"
        v-model:page="page"
        @update:items-per-page="v => { perPage = v; page = 1; fetchProducts() }"
      >
        <template #item.name="{ item }">
          <div class="d-flex align-center gap-3 py-2">
            <VAvatar
              v-if="item.image"
              :image="`/storage/${item.image}`"
              size="40"
              rounded="lg"
            />
            <VAvatar
              v-else
              size="40"
              rounded="lg"
              color="primary"
              variant="tonal"
            >
              <VIcon icon="ri-cup-line" />
            </VAvatar>
            <div>
              <div class="font-weight-medium">{{ item.name }}</div>
              <div class="text-caption text-medium-emphasis">
                {{ getVariantPriceRange(item.variants) }}
              </div>
            </div>
          </div>
        </template>

        <template #item.category="{ item }">
          <span class="text-body-2">{{ item.category?.name ?? '—' }}</span>
        </template>

        <template #item.variants="{ item }">
          <VChip size="small" color="info" variant="tonal">
            {{ item.variants?.length ?? 0 }} varian
          </VChip>
        </template>

        <template #item.is_active="{ item }">
          <VChip
            :color="item.is_active ? 'success' : 'default'"
            variant="tonal"
            size="small"
          >
            {{ item.is_active ? 'Aktif' : 'Non-aktif' }}
          </VChip>
        </template>

        <template #item.actions="{ item }">
          <div class="d-flex gap-1 justify-center">
            <VBtn
      icon
      variant="text"
      size="small"
      color="info"
      :to="{ name: 'catalog-products-id', params: { id: item.id } }"
    >
      <VIcon icon="ri-eye-line" />
      <VTooltip activator="parent">Detail</VTooltip>
    </VBtn>
            <VBtn
              v-if="ability.can('update', 'Product')"
              icon
              variant="text"
              size="small"
              :to="{ name: 'catalog-products-id-edit', params: { id: item.id } }"
            >
              <VIcon icon="ri-pencil-line" />
              <VTooltip activator="parent">Edit</VTooltip>
            </VBtn>
            <VBtn
              v-if="ability.can('delete', 'Product')"
              icon
              variant="text"
              size="small"
              color="error"
              @click="confirmDelete(item)"
            >
              <VIcon icon="ri-delete-bin-line" />
              <VTooltip activator="parent">Hapus</VTooltip>
            </VBtn>
          </div>
        </template>

        <template #no-data>
          <div class="text-center py-10">
            <VIcon icon="ri-box-3-line" size="56" color="medium-emphasis" class="mb-3" />
            <p class="text-body-1 text-medium-emphasis">Belum ada produk</p>
            <VBtn
              v-if="ability.can('create', 'Product')"
              size="small"
              variant="tonal"
              class="mt-2"
              :to="{ name: 'catalog-products-create' }"
            >
              Tambah Produk Pertama
            </VBtn>
          </div>
        </template>
      </VDataTableServer>
    </VCard>

    <!-- Delete dialog -->
    <VDialog
      v-model="deleteDialog"
      max-width="420"
    >
      <VCard>
        <VCardText class="pt-5 pb-3 text-center">
          <VIcon icon="ri-error-warning-line" size="52" color="warning" class="mb-3" />
          <h6 class="text-h6 mb-1">Hapus Produk?</h6>
          <p class="text-body-2 text-medium-emphasis">
            Produk <strong>{{ selectedProduct?.name }}</strong> akan dihapus permanen.
            Jika sudah pernah dijual, produk akan dinonaktifkan otomatis.
          </p>
        </VCardText>
        <VCardActions class="justify-center pb-5 gap-3">
          <VBtn variant="tonal" @click="deleteDialog = false">Batal</VBtn>
          <VBtn color="error" :loading="deleteLoading" @click="doDelete">Hapus</VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <VSnackbar v-model="snackbar.show" :color="snackbar.color" :timeout="3000">
      {{ snackbar.text }}
    </VSnackbar>
  </div>
</template>
